<template>
  <div class="relative flex items-center gap-x-1">
    <RangeInput
      :min="min"
      :max="localMaxValue - 1"
      :value="localMinValue"
      @input="changeMinValue"
    />
    <RangeSlider
      :min-value="localMinValue"
      :max-value="localMaxValue"
      :gap="gap"
      :min="min"
      :max="max"
      class="flex-1"
      @change-min-value="changeRangeMinValue"
      @change-max-value="changeRangeMaxValue"
    />
    <RangeInput
      :min="localMinValue + 1"
      :max="max"
      :value="localMaxValue"
      @input="changeMaxValue"
    />
    <RangeErrorMessage
      v-if="error && errorValue"
      :value="errorValue"
      :title="error"
      :min="min"
      :max="max"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import RangeErrorMessage from '~/components/filters/RangeErrorMessage.vue'
import RangeInput from '~/components/filters/RangeInput.vue'
import RangeSlider from '~/components/filters/RangeSlider.vue'

const props = withDefaults(
  defineProps<{
    min?: number
    max?: number
    gap?: number
    minValue: number
    maxValue: number
  }>(),
  {
    min: 0,
    max: 100,
    gap: 1,
  },
)

const error = ref<string | null>('Issue: Minimum range')
const errorValue = ref<number | null>(null)
const timeout = ref<ReturnType<typeof setTimeout> | null>(null)
const emit = defineEmits(['changeMinValue', 'changeMaxValue'])

const localMinValue = computed({
  get: () => props.minValue,
  set: (value: number) => emit('changeMinValue', value),
})

const localMaxValue = computed({
  get: () => props.maxValue,
  set: (value: number) => emit('changeMaxValue', value),
})

function clearError() {
  error.value = null
  errorValue.value = null
}

function changeMinValue(event: Event) {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value, 10)

  if (timeout.value) clearTimeout(timeout.value)
  clearError()

  if (value < props.min || value > localMaxValue.value - props.gap) {
    target.value = localMinValue.value.toString()
    error.value = 'Issue: Minimum range'
    errorValue.value = localMinValue.value
    timeout.value = setTimeout(clearError, 3000)
    return
  }

  localMinValue.value = value
}

function changeMaxValue(event: Event) {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value, 10)

  if (timeout.value) clearTimeout(timeout.value)
  clearError()

  if (value > props.max || value < localMinValue.value + props.gap) {
    target.value = localMaxValue.value.toString()
    error.value = 'Issue: Maximum range'
    errorValue.value = localMaxValue.value
    timeout.value = setTimeout(clearError, 3000)
    return
  }

  localMaxValue.value = value
}

function changeRangeMinValue(value: number) {
  localMinValue.value = value
}

function changeRangeMaxValue(value: number) {
  localMaxValue.value = value
}
</script>
